import { useEffect, useState, FC } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "rc-slider/assets/index.css";
import DefaultFooter from "../components/default-footer";
import DefaultHeader from "../components/default-header";
import { countries } from "../data/countries.json";

// ---------------------------------------------------------------------
// DUMMY DATA -- no backend calls in this version. Swap DUMMY_TRIP for a
// real GET `${API_URL}/tours/${id}` call, and handleSubmit's fake delay
// for a real POST `${API_URL}/bookings` call, whenever the API is ready.
// ---------------------------------------------------------------------
interface TripPhoto {
  id: string;
  url: string;
  original_name: string;
}

interface TripDetail {
  id: string;
  title: string;
  slug: string;
  location: string;
  country: string;
  price: number;
  currency: string;
  date_from: string;
  date_to: string;
  photos: TripPhoto[];
  highlights: string[];
}

interface GuestCounts {
  Adults: number;
  Children: number;
  Rooms: number;
}

// Carried over from the trip detail page's "Book Now" button, if the
// visitor already picked dates/travelers there.
interface IncomingBookingState {
  dates?: [string, string];
  guestCounts?: GuestCounts;
}

type Step = 1 | 2 | 3;

// Stand-in trip -- shaped exactly like the real TripResource so swapping
// in the live API later is a one-line change.
const DUMMY_TRIP: TripDetail = {
  id: "dummy-trip-01",
  title: "Musandam Fjords & Dolphin Cruise",
  slug: "musandam-fjords-dolphin-cruise",
  location: "Khasab",
  country: "Oman",
  price: 65.0,
  currency: "OMR",
  date_from: "2026-09-10",
  date_to: "2026-09-13",
  photos: [
    {
      id: "photo-1",
      url: "https://images.unsplash.com/photo-1544644181-1484b3fdfc62?w=400&q=80",
      original_name: "musandam.jpg",
    },
  ],
  highlights: [
    "Traditional dhow cruise through the fjords",
    "Dolphin watching",
    "Snorkeling at Telegraph Island",
  ],
};

const counters: { key: keyof GuestCounts; label: string; sub?: string; min: number }[] = [
  { key: "Adults", label: "Adults", min: 1 },
  { key: "Children", label: "Children", sub: "Ages 0–17, billed at 70%", min: 0 },
  { key: "Rooms", label: "Rooms", min: 1 },
];

type CounterProps = {
  label: string;
  sub?: string;
  value: number;
  min: number;
  onChange: (next: number) => void;
};

const Counter: FC<CounterProps> = ({ label, sub, value, min, onChange }) => (
  <div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0" }}>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1a2f38" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#7a9aaa", marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          style={{ width: 30, height: 30, borderRadius: 8, border: "1.5px solid rgba(41,75,90,0.25)", background: "#fff", color: "#294b5a", cursor: "pointer", fontSize: 16 }}
        >
          −
        </button>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#1a2f38", minWidth: 18, textAlign: "center" }}>{value}</span>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "#294b5a", color: "#fff", cursor: "pointer", fontSize: 16 }}
        >
          +
        </button>
      </div>
    </div>
    <div style={{ height: 1, background: "#eef1f3" }} />
  </div>
);

const STEP_LABELS: Record<Step, string> = {
  1: "Trip & travelers",
  2: "Your details",
  3: "Review & confirm",
};

const BookingPage = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const incoming = (location.state as IncomingBookingState) || {};

  const [trip, setTrip] = useState<TripDetail | null>(null);
  const [loadingTrip, setLoadingTrip] = useState(true);
  const [tripError, setTripError] = useState(false);

  const [step, setStep] = useState<Step>(1);
  const [dates, setDates] = useState<DateObject[]>([]);
  const [guestCounts, setGuestCounts] = useState<GuestCounts>(
    incoming.guestCounts || { Adults: 2, Children: 0, Rooms: 1 },
  );

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [nationality, setNationality] = useState("");
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<{ reference: string; total: number; currency: string } | null>(null);

  useEffect(() => {
    // Dummy data only -- no API call. A short delay so the loading state
    // is still visible/testable in the UI.
    setLoadingTrip(true);
    const timer = setTimeout(() => {
      const data = DUMMY_TRIP;
      setTrip(data);
      if (incoming.dates?.length === 2) {
        setDates([new DateObject(incoming.dates[0]), new DateObject(incoming.dates[1])]);
      } else {
        setDates([new DateObject(data.date_from), new DateObject(data.date_to)]);
      }
      setLoadingTrip(false);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pricePerAdult = trip?.price ?? 0;
  const pricePerChild = pricePerAdult * 0.7;
  const estimatedTotal =
    guestCounts.Adults * pricePerAdult + guestCounts.Children * pricePerChild;

  const canContinueStep1 = dates.length === 2 && guestCounts.Adults >= 1;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canContinueStep2 = fullName.trim() !== "" && phone.trim() !== "" && emailValid;

  const goNext = () => setStep((s) => (Math.min(3, s + 1) as Step));
  const goBack = () => setStep((s) => (Math.max(1, s - 1) as Step));

  const handleSubmit = () => {
    if (!trip || dates.length < 2) return;
    setSubmitting(true);
    // Dummy submit -- no API call. Simulates network latency, then shows
    // the confirmation screen with a fake booking reference.
    setTimeout(() => {
      const reference =
        "SJ-" + new Date().toISOString().slice(0, 10).replace(/-/g, "") + "-" +
        Math.random().toString(36).slice(2, 6).toUpperCase();
      setConfirmation({
        reference,
        total: estimatedTotal,
        currency: trip.currency,
      });
      setSubmitting(false);
    }, 900);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');
        .bk-page { font-family: 'Jost', sans-serif; background: #fff; color: #2d3f48; }
        .bk-hero { background: #1a2f38; padding: 40px 0 30px; }
        .bk-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 10px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: #D3BE76; margin-bottom: 10px; }
        .bk-eyebrow::before { content: ''; width: 26px; height: 1.5px; background: #D3BE76; display: block; }
        .bk-hero h1 { font-family: 'Cormorant Garamond', serif; font-size: clamp(24px, 3vw, 36px); font-weight: 700; color: #fff; line-height: 1.2; margin: 0 0 10px; }
        .bk-hero__meta { display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px; color: rgba(255,255,255,0.6); }
        .bk-hero__meta span { display: flex; align-items: center; gap: 6px; }
        .bk-hero__meta i { color: #D3BE76; font-size: 13px; }
        .bk-stepper { display: flex; align-items: center; margin-top: 26px; max-width: 480px; }
        .bk-step { display: flex; align-items: center; gap: 10px; }
        .bk-step__circle { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: 'Cormorant Garamond', serif; font-weight: 700; font-size: 14px; flex-shrink: 0; transition: all 0.2s; }
        .bk-step--done .bk-step__circle { background: #D3BE76; color: #1a2f38; }
        .bk-step--active .bk-step__circle { background: #294b5a; color: #D3BE76; border: 1.5px solid #D3BE76; }
        .bk-step--upcoming .bk-step__circle { background: transparent; color: rgba(255,255,255,0.4); border: 1.5px solid rgba(255,255,255,0.25); }
        .bk-step__label { font-size: 11px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; }
        .bk-step--active .bk-step__label { color: #fff; }
        .bk-step--upcoming .bk-step__label, .bk-step--done .bk-step__label { color: rgba(255,255,255,0.5); }
        .bk-step__label { display: none; }
        @media (min-width: 768px) { .bk-step__label { display: block; } }
        .bk-step__line { flex: 1; height: 1.5px; background: rgba(255,255,255,0.15); margin: 0 10px; min-width: 24px; }
        .bk-step__line--done { background: #D3BE76; }
        .bk-card { background: #fff; border: 1px solid #e8eef1; border-radius: 16px; padding: 28px; }
        .bk-section-title { font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 700; color: #1a2f38; margin-bottom: 4px; }
        .bk-section-sub { font-size: 12.5px; color: #7a9aaa; margin-bottom: 20px; }
        .bk-field { border: 1.5px solid #e0e8ec; border-radius: 12px; padding: 14px 16px; margin-bottom: 12px; background: #faf8f3; transition: border-color 0.2s; }
        .bk-field:focus-within { border-color: #294b5a; background: #fff; }
        .bk-field__label { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #294b5a; margin-bottom: 5px; }
        .bk-form-field { position: relative; }
        .bk-form-field input, .bk-form-field textarea, .bk-form-field select {
          width: 100%; padding: 18px 16px 8px; border: 1.5px solid #e0e8ec; border-radius: 12px;
          font-family: 'Jost', sans-serif; font-size: 14.5px; color: #1a2f38; outline: none;
          background: #faf8f3; resize: none; transition: border-color 0.2s, box-shadow 0.2s; box-sizing: border-box;
        }
        .bk-form-field input:focus, .bk-form-field textarea:focus, .bk-form-field select:focus {
          border-color: #294b5a; background: #fff; box-shadow: 0 0 0 3px rgba(41,75,90,0.07);
        }
        .bk-form-field label { position: absolute; left: 16px; top: 14px; font-size: 13px; color: #7a9aaa; pointer-events: none; transition: all 0.2s; }
        .bk-form-field input:focus ~ label, .bk-form-field input:valid ~ label,
        .bk-form-field select:focus ~ label, .bk-form-field select:valid ~ label,
        .bk-form-field textarea:focus ~ label, .bk-form-field textarea.has-value ~ label,
        .bk-form-field.always-float label {
          top: 5px; font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: #294b5a;
        }
        .bk-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 0 26px; height: 48px; border-radius: 12px; font-family: 'Jost', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; cursor: pointer; border: none; transition: opacity 0.2s, transform 0.2s; }
        .bk-btn:hover { transform: translateY(-1px); }
        .bk-btn--primary { background: linear-gradient(135deg, #294b5a 0%, #1a2f38 100%); color: #fff; }
        .bk-btn--ghost { background: none; border: 1.5px solid rgba(41,75,90,0.22); color: #294b5a; }
        .bk-btn--full { width: 100%; }
        .bk-btn:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }
        .bk-price-widget { background: #fff; border: 1px solid #e8eef1; border-radius: 18px; box-shadow: 0 12px 40px rgba(41,75,90,0.1); overflow: hidden; position: sticky; top: 24px; }
        .bk-price-widget__head { background: #1a2f38; padding: 22px 26px; }
        .bk-price-widget__label { font-size: 11px; color: rgba(255,255,255,0.5); letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 6px; }
        .bk-price-widget__total { font-family: 'Cormorant Garamond', serif; font-size: 32px; font-weight: 700; color: #fff; }
        .bk-price-widget__body { padding: 20px 26px; }
        .bk-price-line { display: flex; justify-content: space-between; font-size: 13px; color: #4a6370; padding: 6px 0; }
        .bk-trust { display: flex; gap: 10px; padding: 16px 26px 20px; background: rgba(211,190,118,0.06); border-top: 1px solid #eef1f3; }
        .bk-trust i { color: #D3BE76; font-size: 17px; margin-top: 1px; }
        .bk-trust span { font-size: 12px; color: #7a9aaa; line-height: 1.6; }
        .bk-summary-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px dashed #e8eef1; font-size: 13.5px; }
        .bk-summary-row:last-child { border-bottom: none; }
        .bk-summary-row span:first-child { color: #7a9aaa; }
        .bk-summary-row span:last-child { color: #1a2f38; font-weight: 600; text-align: right; }
        .bk-confirm { text-align: center; max-width: 460px; margin: 0 auto; padding: 60px 20px 90px; }
        .bk-confirm__icon { width: 68px; height: 68px; border-radius: 50%; background: #D3BE76; color: #1a2f38; display: flex; align-items: center; justify-content: center; margin: 0 auto 22px; font-size: 28px; }
        .bk-confirm h2 { font-family: 'Cormorant Garamond', serif; font-size: 30px; font-weight: 700; color: #1a2f38; margin: 0 0 10px; }
        .bk-confirm p { font-size: 14.5px; color: #4a6370; line-height: 1.7; margin: 0 0 24px; }
        .bk-ref-chip { display: inline-block; padding: 10px 22px; border: 1.5px dashed rgba(41,75,90,0.3); border-radius: 10px; font-family: 'Cormorant Garamond', serif; font-size: 20px; font-weight: 700; color: #294b5a; letter-spacing: 1px; margin-bottom: 26px; }
      `}</style>

      <div className="bk-page">
        <div className="header-margin"></div>
        <DefaultHeader />

        {loadingTrip ? (
          <div style={{ padding: "100px 0", textAlign: "center", color: "#7a9aaa", fontSize: 14 }}>
            Loading trip...
          </div>
        ) : tripError || !trip ? (
          <div style={{ padding: "100px 20px", textAlign: "center" }}>
            <div className="bk-section-title" style={{ marginBottom: 10 }}>This trip isn't available</div>
            <p style={{ color: "#7a9aaa", fontSize: 14, marginBottom: 20 }}>
              It may have been unpublished or the link is out of date.
            </p>
            <Link to="/tours" className="bk-btn bk-btn--primary">Browse tours</Link>
          </div>
        ) : confirmation ? (
          <div className="container">
            <div className="bk-confirm">
              <div className="bk-confirm__icon">✓</div>
              <h2>Request received</h2>
              <p>
                Thanks, {fullName.split(" ")[0]}. Our team will reach out within 24 hours to confirm
                availability for <strong>{trip.title}</strong> and arrange payment with you directly.
              </p>
              <div className="bk-ref-chip">{confirmation.reference}</div>
              <p style={{ marginTop: -14 }}>
                Estimated total: {confirmation.currency} {confirmation.total.toFixed(3)}
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <Link to="/tours" className="bk-btn bk-btn--ghost">Browse more tours</Link>
                <Link to="/" className="bk-btn bk-btn--primary">Return home</Link>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* HERO + STEPPER */}
            <div className="bk-hero">
              <div className="container">
                <div className="bk-eyebrow">Complete your booking</div>
                <h1>{trip.title}</h1>
                <div className="bk-hero__meta">
                  <span><i className="icon-placeholder" />{trip.location}, {trip.country}</span>
                  <span><i className="icon-clock" />{trip.date_from} – {trip.date_to}</span>
                </div>

                <div className="bk-stepper">
                  {([1, 2, 3] as Step[]).map((s, i) => {
                    const state = s < step ? "done" : s === step ? "active" : "upcoming";
                    return (
                      <>
                        <div key={s} className={`bk-step bk-step--${state}`}>
                          <div className="bk-step__circle">{state === "done" ? "✓" : s}</div>
                          <div className="bk-step__label">{STEP_LABELS[s]}</div>
                        </div>
                        {i < 2 && <div className={`bk-step__line ${s < step ? "bk-step__line--done" : ""}`} />}
                      </>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* MAIN */}
            <section style={{ padding: "40px 0 90px" }}>
              <div className="container">
                <div className="row y-gap-30">
                  <div className="col-xl-8">
                    <div className="bk-card">
                      {step === 1 && (
                        <>
                          <div className="bk-section-title">When are you traveling?</div>
                          <div className="bk-section-sub">Adjust dates and travelers -- the price updates as you go.</div>

                          <div style={{ display: "flex", gap: 14, alignItems: "center", background: "#faf8f3", borderRadius: 12, padding: 14, marginBottom: 20 }}>
                            <div style={{ width: 56, height: 56, borderRadius: 10, overflow: "hidden", flexShrink: 0, background: "#294b5a" }}>
                              {trip.photos?.[0] && (
                                <img src={trip.photos[0].url} alt={trip.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              )}
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#1a2f38" }}>{trip.title}</div>
                              <div style={{ fontSize: 12, color: "#7a9aaa", marginTop: 2 }}>
                                {trip.currency} {trip.price.toFixed(3)} per adult
                              </div>
                            </div>
                          </div>

                          <div className="bk-field">
                            <div className="bk-field__label">Travel dates</div>
                            <DatePicker
                              value={dates}
                              onChange={(value) => setDates(value as DateObject[])}
                              numberOfMonths={2}
                              offsetY={10}
                              minDate={new DateObject()}
                              range
                              rangeHover
                              format="MMM DD"
                              inputClass="custom_input-picker"
                              containerClassName="custom_container-picker"
                            />
                          </div>

                          <div style={{ border: "1.5px solid #e0e8ec", borderRadius: 12, padding: "4px 16px", background: "#faf8f3" }}>
                            {counters.map((c) => (
                              <Counter
                                key={c.key}
                                label={c.label}
                                sub={c.sub}
                                min={c.min}
                                value={guestCounts[c.key]}
                                onChange={(next) => setGuestCounts((p) => ({ ...p, [c.key]: next }))}
                              />
                            ))}
                          </div>

                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
                            <button type="button" className="bk-btn bk-btn--primary" disabled={!canContinueStep1} onClick={goNext}>
                              Continue
                            </button>
                          </div>
                        </>
                      )}

                      {step === 2 && (
                        <>
                          <div className="bk-section-title">Your details</div>
                          <div className="bk-section-sub">We'll use this to confirm availability and reach you -- nothing else.</div>

                          <div className="row y-gap-16">
                            <div className="col-md-6">
                              <div className="bk-form-field">
                                <input type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
                                <label>Full name</label>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="bk-form-field">
                                <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} />
                                <label>Phone</label>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="bk-form-field">
                                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                                <label>Email address</label>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="bk-form-field always-float">
                                <select value={nationality} onChange={(e) => setNationality(e.target.value)}>
                                  <option value="">Select...</option>
                                  {(countries as string[] | undefined)?.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                                <label>Nationality (optional)</label>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="bk-form-field">
                                <textarea
                                  rows={4}
                                  value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                  className={notes ? "has-value" : ""}
                                />
                                <label>Special requests (optional)</label>
                              </div>
                            </div>
                          </div>

                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
                            <button type="button" className="bk-btn bk-btn--ghost" onClick={goBack}>Back</button>
                            <button type="button" className="bk-btn bk-btn--primary" disabled={!canContinueStep2} onClick={goNext}>
                              Continue
                            </button>
                          </div>
                        </>
                      )}

                      {step === 3 && (
                        <>
                          <div className="bk-section-title">Review your request</div>
                          <div className="bk-section-sub">Nothing is charged now -- confirm below to send this to our team.</div>

                          <div className="bk-summary-row"><span>Trip</span><span>{trip.title}</span></div>
                          <div className="bk-summary-row">
                            <span>Dates</span>
                            <span>{dates[0]?.format("MMM DD")} – {dates[1]?.format("MMM DD, YYYY")}</span>
                          </div>
                          <div className="bk-summary-row">
                            <span>Travelers</span>
                            <span>{guestCounts.Adults} adults · {guestCounts.Children} children · {guestCounts.Rooms} room</span>
                          </div>
                          <div className="bk-summary-row"><span>Name</span><span>{fullName}</span></div>
                          <div className="bk-summary-row"><span>Contact</span><span>{phone} · {email}</span></div>
                          {notes && <div className="bk-summary-row"><span>Notes</span><span>{notes}</span></div>}

                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
                            <button type="button" className="bk-btn bk-btn--ghost" onClick={goBack} disabled={submitting}>Back</button>
                            <button type="button" className="bk-btn bk-btn--primary" onClick={handleSubmit} disabled={submitting}>
                              {submitting ? "Sending..." : "Request to book"}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* PRICE SUMMARY -- persistent across all steps */}
                  <div className="col-xl-4">
                    <div className="bk-price-widget">
                      <div className="bk-price-widget__head">
                        <div className="bk-price-widget__label">Estimated total</div>
                        <div className="bk-price-widget__total">
                          {trip.currency} {estimatedTotal.toFixed(3)}
                        </div>
                      </div>
                      <div className="bk-price-widget__body">
                        <div className="bk-price-line">
                          <span>{guestCounts.Adults} adults × {trip.currency} {pricePerAdult.toFixed(3)}</span>
                          <span>{(guestCounts.Adults * pricePerAdult).toFixed(3)}</span>
                        </div>
                        {guestCounts.Children > 0 && (
                          <div className="bk-price-line">
                            <span>{guestCounts.Children} children × {trip.currency} {pricePerChild.toFixed(3)}</span>
                            <span>{(guestCounts.Children * pricePerChild).toFixed(3)}</span>
                          </div>
                        )}
                      </div>
                      <div className="bk-trust">
                        <i className="icon-shield" />
                        <span>No payment now. Our team confirms availability and arranges payment with you directly.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        <DefaultFooter />
      </div>
      <ToastContainer />
    </>
  );
};

export default BookingPage;